"use client";

import { Check, ArrowRight } from "@phosphor-icons/react/dist/ssr";
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from "react";

import { Container } from "@/src/components/ui/Container";
import Section from "@/src/components/ui/Section";
import { Heading, Text } from "@/src/components/ui/Typography";
import { Counter } from "@/src/components/ui/Counter";
import { cn } from "@/src/lib/utils";

// Stagger step between each checklist row's reveal within a column — kept
// in one place so the row and its badge/ping delays can't drift apart.
const REVEAL_STEP_SECONDS = 0.12;

export interface OpportunityShiftRow {
  label: string;
  before: string;
  after: string;
  /** Numeric value to animate as the "after" figure — omit for non-numeric rows. */
  afterValue?: number;
  afterSuffix?: string;
}

export interface OpportunityImpactGroup {
  title: string;
  bullets: string[];
}

export interface OpportunityNarrativeSection {
  title: string;
  body: ReactNode;
}

export interface OpportunityShiftProps {
  title: string;
  /** "Instead of X, what if Y" framing lines shown above the body. */
  prompts?: string[];
  /** Before → after comparison grid (numeric "after" values animate via Counter). */
  rows?: OpportunityShiftRow[];
  /** Grouped checklist columns — for content shaped as outcome bullets rather than before/after pairs. */
  impactGroups?: OpportunityImpactGroup[];
  /** Titled paragraph columns — for content shaped as a short explainer per theme. */
  narrativeSections?: OpportunityNarrativeSection[];
  calloutValue?: string;
  calloutLabel?: string;
}

export function OpportunityShift({
  title,
  prompts,
  rows,
  impactGroups,
  narrativeSections,
  calloutValue,
  calloutLabel,
}: OpportunityShiftProps) {
  const [revealed, setRevealed] = useState(false);
  const impactRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const node = impactRef.current;
    if (!node) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setRevealed(true);
        observer.disconnect();
      },
      { threshold: 0.3 },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <Section className="bg-white">
      <Container>
        <Heading as="h2" size="displayLG" className="max-w-175">
          {title}
        </Heading>

        {prompts && prompts.length > 0 && (
          <div className="mt-8 flex flex-col gap-6 max-w-175">
            {prompts.map((prompt, index) => (
              <Heading key={index} size="headingMD" className="text-neutral-700">
                {prompt}
              </Heading>
            ))}
          </div>
        )}

        {rows && rows.length > 0 && (
          <div className="mt-13 grid gap-x-8 gap-y-6 border-t border-neutral-200 pt-8 sm:grid-cols-2 md:mt-16 lg:grid-cols-4">
            {rows.map((row) => (
              <div key={row.label} className="flex flex-col gap-3">
                <Text size="bodySMMedium" className="text-neutral-600">
                  {row.label}
                </Text>

                <div className="flex items-center gap-3">
                  <Text size="bodyMD" className="text-neutral-400 line-through">
                    {row.before}
                  </Text>

                  <ArrowRight size={16} weight="regular" className="shrink-0 text-neutral-400" />

                  <Text as="span" size="titleMD" className="text-neutral-700">
                    {row.afterValue !== undefined ? (
                      <Counter value={row.afterValue} suffix={row.afterSuffix} />
                    ) : (
                      row.after
                    )}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        )}

        {impactGroups && impactGroups.length > 0 && (
          <div
            ref={impactRef}
            className="mt-13 grid gap-x-8 gap-y-10 border-t border-neutral-200 pt-8 md:mt-16 md:grid-cols-3"
          >
            {impactGroups.map((group) => (
              <div key={group.title}>
                <Heading as="h3" size="headingMD" className="text-neutral-700">
                  {group.title}
                </Heading>

                <div className="mt-5 flex flex-col gap-3">
                  {group.bullets.map((bullet, index) => {
                    const revealDelay = {
                      "--reveal-delay": `${index * REVEAL_STEP_SECONDS}s`,
                    } as CSSProperties;

                    return (
                      <div
                        key={index}
                        style={revealDelay}
                        className={cn(
                          "flex items-start gap-3 opacity-0",
                          revealed && "checklist-item-reveal",
                        )}
                      >
                        <span
                          style={revealDelay}
                          className={cn(
                            "relative mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand opacity-0",
                            revealed && "checklist-badge-pop checklist-ping",
                          )}
                        >
                          <Check size={12} weight="bold" className="relative z-10" />
                        </span>

                        <Text size="bodySM" className="text-neutral-600">
                          {bullet}
                        </Text>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {narrativeSections && narrativeSections.length > 0 && (
          <div className="mt-13 grid gap-10 border-t border-neutral-200 pt-8 md:mt-16 md:grid-cols-3 md:gap-13">
            {narrativeSections.map((section) => (
              <div key={section.title}>
                <Heading as="h3" size="headingMD" className="text-neutral-700">
                  {section.title}
                </Heading>

                <Text size="bodySM" className="mt-3 text-neutral-600">
                  {section.body}
                </Text>
              </div>
            ))}
          </div>
        )}

        {calloutValue && calloutLabel && (
          <div
            data-dark-section
            className="mt-13 md:mt-16 flex flex-col gap-4 rounded-(--radius-xl) bg-neutral-900 py-10.5 pr-8 pl-8 sm:flex-row sm:items-center sm:gap-13 md:pr-18 md:pl-13"
          >
            <Heading as="h4" size="displayLG" className="shrink-0 text-neutral-50">
              {calloutValue}
            </Heading>

            <span
              aria-hidden
              className="hidden h-full min-h-12 w-px flex-none bg-neutral-50 sm:block"
            />
            <span aria-hidden className="block h-px w-full flex-none bg-neutral-50 sm:hidden" />

            <Text size="bodyLG" className="text-neutral-200">
              {calloutLabel}
            </Text>
          </div>
        )}
      </Container>
    </Section>
  );
}
